/* eslint-disable max-statements */
import { adjustmentType } from '../enums';
import { buildMatchableItemsCfn, matchesRequiredSku } from './build-matchable-items.cfn';
import { PromotionalSku } from '../promotional-sku.class';


export const totalAdjustmentCfn = (total: number, promotionalSku: PromotionalSku): number => {
	const { sku, requiredSkus, priceAdjustment, adjustmentType: skuAdjustmentType, hasDiscountRules, discountRules } = promotionalSku;

	if (sku === 'SHIPPING' || sku === 'FREE-SHIPPING') return 0;

	// Skip promotions that ONLY have ISLAND/ISLAND_BASE targets (handled in getItemizedMsrpCfn)
	const islandTargets = new Set(['ISLAND', 'ISLAND_BASE']);
	const hasOnlyIslandTargets = hasDiscountRules
		? discountRules.every((rule) => islandTargets.has(rule.target))
		: requiredSkus && requiredSkus.length > 0 && requiredSkus.every((rs) => islandTargets.has(rs));

	if (hasOnlyIslandTargets) return 0;

	const { kitchen } = promotionalSku.refMap;

	// discountRules path: per-rule adjustments with independent adjustmentTypes
	if (hasDiscountRules) {
		const allItems = buildMatchableItemsCfn(kitchen);
		let totalDiscount = 0;
		const breakdown: Record<string, number> = {};

		// A single-rule promotion stores its amount twice -- once on the rule, once on the sku -- and the
		// price box edits the sku copy, so that copy is authoritative. Multi-rule promotions keep their
		// own per-rule amounts: one typed number cannot say what each of several targets should become.
		const singleRuleAmount = discountRules.length === 1 ? priceAdjustment : null;

		discountRules.forEach((rule) => {
			const { target, adjustmentType: ruleType } = rule;
			const ruleAdjustment = singleRuleAmount ?? rule.priceAdjustment;

			// ISLAND and ISLAND_BASE rules are handled in getItemizedMsrpCfn
			if (islandTargets.has(target)) return;

			let ruleTotal = 0;

			if (ruleType === adjustmentType.Fixed) {
				allItems.forEach((item) => {
					if (matchesRequiredSku(item, target)) {
						ruleTotal += ruleAdjustment;
					}
				});
			} else if (ruleType === adjustmentType.Percentage) {
				allItems.forEach((item) => {
					if (matchesRequiredSku(item, target)) {
						ruleTotal += item.total * (ruleAdjustment / 100);
					}
				});
			}

			totalDiscount += ruleTotal;
			breakdown[target] = (breakdown[target] ?? 0) + ruleTotal;
		});

		// Merge with existing breakdown (ISLAND/ISLAND_BASE portions set in getItemizedMsrpCfn)
		const { ruleBreakdown } = promotionalSku;
		promotionalSku.ruleBreakdown = { ...ruleBreakdown, ...breakdown };

		// totalAdjustment includes ALL rule portions (island + non-island) for display
		const islandPortion = (promotionalSku.ruleBreakdown.ISLAND ?? 0)
			+ (promotionalSku.ruleBreakdown.ISLAND_BASE ?? 0);
		promotionalSku.totalAdjustment = totalDiscount + islandPortion;
		// Return only non-island portion (island already applied in getItemizedMsrpCfn)
		return totalDiscount;
	}

	// Legacy path: single priceAdjustment applied uniformly
	if (requiredSkus && requiredSkus.some((rs) => islandTargets.has(rs))) return 0;

	if (skuAdjustmentType === adjustmentType.Fixed) {
		const discountedItems = [];

		if (requiredSkus && requiredSkus.length > 0) {
			const allItems = buildMatchableItemsCfn(kitchen);
			allItems.forEach((item) => {
				if (requiredSkus.some((rs) => matchesRequiredSku(item, rs))) {
					discountedItems.push(item.total + priceAdjustment);
				}
			});
		}
		const baseAmount = discountedItems.length > 0
			? (priceAdjustment * discountedItems.length)
			: priceAdjustment;
		const fixedAdjustment = baseAmount * (promotionalSku.quantity ?? 1);
		promotionalSku.totalAdjustment = fixedAdjustment;
		return fixedAdjustment;
	} else if (skuAdjustmentType === adjustmentType.Percentage) {
		let discountedItems = 0;

		if (requiredSkus && requiredSkus.length > 0) {
			const allItems = buildMatchableItemsCfn(kitchen);
			allItems.forEach((item) => {
				if (requiredSkus.some((rs) => matchesRequiredSku(item, rs))) {
					discountedItems += item.total * (priceAdjustment / 100);
				}
			});
		}
		if (discountedItems !== 0) {
			promotionalSku.totalAdjustment = discountedItems;
			return discountedItems;
		}

		// Calculate the percentage of the totalPrice
		const percentageAdjustment = total * (priceAdjustment / 100);
		promotionalSku.totalAdjustment = percentageAdjustment;
		return percentageAdjustment;
	}
	return 0
};
